import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    input,
    model,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    SimpleChanges,
    viewChild,
    ViewEncapsulation
} from '@angular/core';
import { Router } from '@angular/router';
import {
    BaseOrder,
    DocumentFile,
    ORDER_SUBMISSION_NOTE_MAX_LENGTH
} from '@trade-platform/ui-shared';
import { Subscription } from 'rxjs';
import { commentTag, orderStatus } from '@trade-platform/lib-enums';
import { AixFileUploadComponent } from '../../../../file-upload/file-upload';
import {
    BaseOrdersStoreFacade,
    getRequiredFilesToUpload,
    getUnfinishedRequiredUploads,
    getUnlabeledSupplementalUploads,
    getUploadedSupplementalFiles,
    isAboveTotalFileSizeLimit,
    OrderFormCommentsPipe,
    ORDERS_STORE_FACADE
} from '@advisor-ui/orders';
import {
    AixButtonComponent,
    AixDataTestingDirective,
    AixHeaderSectionComponent,
    AixModalComponent,
    AixNotificationComponent,
    AixTextareaComponent,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import { AixFormlistComponent, FormListItem } from '../../formlist/formlist';
import { AsyncPipe } from '@angular/common';
import { getFileRouteId } from '../../../../utils';

@Component({
    selector: 'aix-order-submit-confirmation',
    templateUrl: './submit-confirmation.html',
    styleUrls: ['./submit-confirmation.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AixDataTestingDirective,
        AixHeaderSectionComponent,
        AixFormlistComponent,
        AixButtonComponent,
        AixFileUploadComponent,
        AixModalComponent,
        AsyncPipe,
        OrderFormCommentsPipe,
        AixNotificationComponent,
        AixTextareaComponent
    ]
})
export class AixOrderSubmitConfirmationComponent implements OnInit, OnChanges, OnDestroy {
    private ref = inject(ChangeDetectorRef);
    store = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    router = inject(Router);

    orderFileUpload = viewChild<AixFileUploadComponent>(AixFileUploadComponent);
    submitOrderModal = viewChild<AixModalComponent>(AixModalComponent);
    manualSubmissionModal = viewChild<AixModalComponent>(AixModalComponent);
    notificationRef = viewChild<AixNotificationComponent>(AixNotificationComponent);

    isSubmitted = model<boolean>();
    isReadOnly = input<boolean>(false);
    approvalType = input<'electronicSubmission' | 'pendingManualSubmission' | 'taSubmission'>();

    showPdf = output<void>();
    onDownloadTradePackage = output();

    isSending = false;
    isSubmitDisabled = false;
    showSupportingDocuments = false;
    unfinishedRequiredUploads = false;
    isAboveTotalFileSize = false;

    error: string | null = null;

    /**
     * Optional note the advisor may attach on the finalize step. The API records it as an
     * information comment on the order. refs trade-platform#10392
     */
    note = '';
    isNoteTooLong = false;
    readonly noteMaxLength = ORDER_SUBMISSION_NOTE_MAX_LENGTH;

    subscriptions: Subscription[] = [];

    commentTag = commentTag;

    literals = {
        electronicSubmission: {
            title: `This order has been eSigned. Please finalize and submit the order.`,
            titleWetSign: `This order has been Downloaded & Signed. Please finalize and submit the order.`,
            completedError: `There was an error sending the order for reviewal. Please try again.`,
            buttonText: `Submit`
        },
        taSubmission: {
            title: `This order has been eSigned. Please finalize and submit the order.`,
            titleWetSign: `This order has been Downloaded & Signed. Please finalize and submit the order.`,
            completedError: `There was an error sending the order for reviewal. Please try again.`,
            buttonText: `Submit`
        },
        pendingManualSubmission: {
            title: `This order has been eSigned. Please download order and submit according to your firm's processes.`,
            titleWetSign: `Please download and have the paperwork signed and submitted based on your firm’s processes.`,
            completedError: `There was an error completing the order. Please try again.`,
            buttonText: `Submit Offline`
        }
    };

    uploadDocumentsButtonType = BUTTON_TYPE.secondary;
    showConfirmationButtonType = BUTTON_TYPE.primary;

    readonly orderStatus = orderStatus;

    orderNumber = '';

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    ngOnInit() {
        this.subscriptions.push(
            this.store.actions.submit.success$.subscribe(action => {
                this.isSubmitted.set(true);
                if (this.approvalType() === 'pendingManualSubmission') {
                    this.onDownloadTradePackage.emit();
                }
                this.ref.detectChanges();
                this.navigateToDetail();
            }),
            this.store.actions.approveStart.success$.subscribe(action => {
                this.isSubmitted.set(true);
                this.ref.detectChanges();
                this.navigateToDetail();
            }),
            this.store.actions.getOrder.success$.subscribe(() => {
                this.notificationRef()?.closeNotification();
                const order: BaseOrder = this.store.order;
                this.orderNumber = order.orderNumber.toString(10);
                this.checkOrder(order);
            }),
            this.store.actions.removeDocument.success$.subscribe(() => {
                const order: BaseOrder = this.store.order;
                this.checkOrder(order);
            })
        );

        const ord: BaseOrder = this.store.order;
        this.checkOrder(ord);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.isSubmitted && changes.isSubmitted.currentValue) {
            this.navigateToDetail();
        }
    }

    navigateToDetail() {
        this.router.navigate(this.store.routes.detailOverview(this.store.order.id));
    }

    /**
     * Validated here rather than with a `maxlength` attribute so an over-long paste is visibly
     * refused instead of being silently clipped.
     */
    onNoteChange(value: string) {
        this.note = value ?? '';
        this.isNoteTooLong = this.note.trim().length > this.noteMaxLength;
        this.ref.detectChanges();
    }

    showConfirmation() {
        if (this.isNoteTooLong) {
            return;
        }
        if (this.isSubmitDisabled || this.store.fileUpload.uploading) {
            this.notificationRef()?.openNotification();
        } else {
            if (
                this.store.order &&
                this.store.order.status === orderStatus.pendingManualSubmission
            ) {
                this.manualSubmissionModal()?.openModal();
            } else {
                this.submitOrderModal()?.openModal();
            }
        }
    }

    submitOrderModalSelected(option: string) {
        if (option === 'Yes, continue') {
            this.submitOrder();
        } else {
            this.submitOrderModal()?.closeModal();
        }
    }

    goToForm(item?: FormListItem) {
        // formlist emits an item whose `document` is undefined when the form has no file on the
        // order, and the cast there hides that from the compiler. refs #7593
        if (!item?.document) {
            return;
        }

        this.router.navigate(
            this.store.routes.documentViewer(this.store.order.id, getFileRouteId(item.document))
        );
    }

    checkOrder(order: BaseOrder) {
        if (order) {
            this.isAboveTotalFileSize = isAboveTotalFileSizeLimit(order);
            this.unfinishedRequiredUploads = getUnfinishedRequiredUploads(order) > 0;
            this.showSupportingDocuments =
                !!getUploadedSupplementalFiles(order).length ||
                !!getRequiredFilesToUpload(order).length;

            this.isSubmitDisabled =
                getUnfinishedRequiredUploads(order) > 0 ||
                getUnlabeledSupplementalUploads(order) > 0;

            this.ref.detectChanges();
        }
    }

    uploadDocuments() {
        this.orderFileUpload()?.uploadDocuments();
    }

    removeDocument(file: DocumentFile) {
        const orderId = this.store.order.id;
        this.store.actions.removeDocument.dispatch({
            orderId,
            fileToRemove: file
        });
    }

    submitOrder() {
        const orderId = this.store.order.id;
        const note = this.note.trim() || undefined;
        this.error = null;
        this.isSending = true;
        if (
            this.approvalType() === 'taSubmission' ||
            this.approvalType() === 'pendingManualSubmission'
        ) {
            this.store.actions.submit.dispatch({ orderId: orderId, note });
        } else {
            this.store.actions.approveStart.dispatch({ orderId: orderId, note });
        }
    }

    onClickViewPdf() {
        this.showPdf.emit();
    }

    onClickGoToOrders() {
        this.router.navigate(this.store.routes.status());
    }

    ngOnDestroy() {
        this.store.actions.approveStart.reset();
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
